import { useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Switch,
	TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import type {
	CatalogOption,
	OrderStatusOption,
	PurchaseOrder,
	UpsertPurchaseOrderMasterParams,
} from "../models/PurchaseOrders";

interface Props {
	open: boolean;
	purchaseOrder: PurchaseOrder | null;
	suppliers: CatalogOption[];
	costCenters: CatalogOption[];
	orderStatuses: OrderStatusOption[];
	isSaving: boolean;
	onClose: () => void;
	onSave: (params: UpsertPurchaseOrderMasterParams) => void;
}

interface FormState {
	Number: string;
	RequisitionNumber: string;
	SupplierId: number | null;
	CostCenterId: number | null;
	OrderStatusCode: string | null;
	DateOrdered: string;
	Active: boolean;
}

const EMPTY: FormState = {
	Number: "",
	RequisitionNumber: "",
	SupplierId: null,
	CostCenterId: null,
	OrderStatusCode: null,
	DateOrdered: "",
	Active: true,
};

// DateOrdered from the SP is a datetime; keep only the yyyy-MM-dd part.
const toDateInput = (value: string | null): string =>
	value ? value.slice(0, 10) : "";

export const PurchaseOrderModal = ({
	open,
	purchaseOrder,
	suppliers,
	costCenters,
	orderStatuses,
	isSaving,
	onClose,
	onSave,
}: Props) => {
	const [form, setForm] = useState<FormState>(EMPTY);

	useEffect(() => {
		if (!open) return;
		if (purchaseOrder) {
			setForm({
				Number: purchaseOrder.Number ?? "",
				RequisitionNumber: purchaseOrder.RequisitionNumber ?? "",
				SupplierId: purchaseOrder.SupplierId ?? null,
				CostCenterId: purchaseOrder.CostCenterId ?? null,
				OrderStatusCode: purchaseOrder.OrderStatusCode ?? null,
				DateOrdered: toDateInput(purchaseOrder.DateOrdered),
				Active: purchaseOrder.Active ?? true,
			});
		} else {
			// New order defaults Date Ordered to today.
			setForm({ ...EMPTY, DateOrdered: moment().format("YYYY-MM-DD") });
		}
	}, [open, purchaseOrder]);

	const selectedSupplier = useMemo(
		() => suppliers.find((s) => s.Id === form.SupplierId) ?? null,
		[suppliers, form.SupplierId],
	);
	const selectedCostCenter = useMemo(
		() => costCenters.find((c) => c.Id === form.CostCenterId) ?? null,
		[costCenters, form.CostCenterId],
	);
	const selectedStatus = useMemo(
		() => orderStatuses.find((o) => o.Code === form.OrderStatusCode) ?? null,
		[orderStatuses, form.OrderStatusCode],
	);

	// Legacy allows editing the Order Number only while the PO has no items.
	const isNumberLocked = !!purchaseOrder && (purchaseOrder.ItemCount ?? 0) > 0;

	const canSave = form.Number.trim().length > 0 && !isSaving;

	const handleSave = () => {
		onSave({
			Id: purchaseOrder?.Id ?? null,
			Active: form.Active,
			Number: form.Number.trim(),
			RequisitionNumber: form.RequisitionNumber.trim() || null,
			SupplierId: form.SupplierId,
			CostCenterId: form.CostCenterId,
			OrderStatusCode: form.OrderStatusCode,
			DateOrdered: form.DateOrdered || null,
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{purchaseOrder ? "Edit Purchase Order" : "New Purchase Order"}
			</DialogTitle>
			<DialogContent dividers>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<TextField
						label="Order Number"
						size="small"
						required
						disabled={isNumberLocked}
						helperText={
							isNumberLocked
								? "Order Number can't be changed once the order has items."
								: undefined
						}
						value={form.Number}
						onChange={(e) => setForm({ ...form, Number: e.target.value })}
					/>
					<TextField
						label="Requisition Number"
						size="small"
						value={form.RequisitionNumber}
						onChange={(e) =>
							setForm({ ...form, RequisitionNumber: e.target.value })
						}
					/>
					<Autocomplete
						size="small"
						options={suppliers}
						value={selectedSupplier}
						getOptionLabel={(o) => o.Name}
						isOptionEqualToValue={(o, v) => o.Id === v.Id}
						onChange={(_e, option) =>
							setForm({ ...form, SupplierId: option?.Id ?? null })
						}
						renderInput={(params) => <TextField {...params} label="Supplier" />}
					/>
					<Autocomplete
						size="small"
						options={costCenters}
						value={selectedCostCenter}
						getOptionLabel={(o) => o.Name}
						isOptionEqualToValue={(o, v) => o.Id === v.Id}
						onChange={(_e, option) =>
							setForm({ ...form, CostCenterId: option?.Id ?? null })
						}
						renderInput={(params) => (
							<TextField {...params} label="Cost Center" />
						)}
					/>
					<Autocomplete
						size="small"
						options={orderStatuses}
						value={selectedStatus}
						getOptionLabel={(o) => o.Name}
						isOptionEqualToValue={(o, v) => o.Code === v.Code}
						onChange={(_e, option) =>
							setForm({ ...form, OrderStatusCode: option?.Code ?? null })
						}
						renderInput={(params) => (
							<TextField {...params} label="Order Status" />
						)}
					/>
					<DatePicker
						label="Date Ordered"
						format="MM/DD/YYYY"
						value={form.DateOrdered ? moment(form.DateOrdered) : null}
						onChange={(d) =>
							setForm({
								...form,
								DateOrdered: d ? d.format("YYYY-MM-DD") : "",
							})
						}
						slotProps={{ textField: { size: "small", fullWidth: true } }}
					/>
					<FormControlLabel
						control={
							<Switch
								checked={form.Active}
								onChange={(e) => setForm({ ...form, Active: e.target.checked })}
							/>
						}
						label="Active"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSave} disabled={!canSave}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
